import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Fade,
  Slide,
  Grow,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { size, capitalize } from "lodash";
import clsx from "classnames";

import ButtonSmg from "../../components/ButtonSmg";

import { useStyles } from "../../hooks";
import styles from "./styles";

const variantTransitionsComponent = {
  fade: Fade,
  slide: Slide,
  grow: Grow,
  zoom: Zoom
};

const ModalSmg = ({
  id,
  open,
  title,
  maxWidth = "xs",
  scroll = "paper",
  TransitionComponent = "fade",
  children,
  disableBackdropClick = true,
  disableEscapeKeyDown = true,
  closeIconButton = false,
  noActions = false,
  onClose,
  ButtonsProps = [],
  dividers = "actions",
  ...otherProps
}) => {
  const classes = useStyles(styles);

  const TransitionElement = useMemo(
    () => variantTransitionsComponent[TransitionComponent],
    [TransitionComponent]
  );
  const ariaDescribedby = `modal-${id}-dialog-title`;
  const ariaLabelledby = `modal-${id}-dialog-description"`;

  const handleClose = useCallback(
    (event, reason) => {
      if (disableBackdropClick && reason === "backdropClick") {
        return;
      }
      if (disableEscapeKeyDown && reason === "escapeKeyDown") {
        return;
      }
      if (onClose) {
        onClose(event, reason);
      }
    },
    [disableBackdropClick, disableEscapeKeyDown, onClose]
  );

  return (
    <Dialog
      maxWidth={maxWidth}
      open={open}
      TransitionComponent={TransitionElement}
      onClose={handleClose}
      scroll={scroll}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
      PaperProps={{
        className: clsx(
          classes.paper,
          classes[`dividers${capitalize(dividers)}`]
        )
      }}
      {...otherProps}
    >
      {title && (
        <DialogTitle id={ariaLabelledby} disableTypography>
          <Typography component="h1" className={classes.title}>
            {title}
          </Typography>
          {closeIconButton ? (
            <IconButton
              aria-label="close"
              onClick={handleClose}
              className={classes.closeButton}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </DialogTitle>
      )}
      <DialogContent id={ariaDescribedby}>{children}</DialogContent>
      {!noActions && size(ButtonsProps) > 0 && (
        <DialogActions className={classes.actions}>
          {ButtonsProps.map(btn => (
            <ButtonSmg
              key={btn.id}
              {...btn}
              className={clsx(classes.button, btn.className)}
            />
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

ModalSmg.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl", false]),
  scroll: PropTypes.oneOf(["body", "paper"]),
  TransitionComponent: PropTypes.oneOf(["fade", "slide", "grow", "zoom"]),
  children: PropTypes.node.isRequired,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  closeIconButton: PropTypes.bool,
  noActions: PropTypes.bool,
  onClose: PropTypes.func,
  ButtonsProps: PropTypes.arrayOf(PropTypes.object.isRequired),
  dividers: PropTypes.oneOf(["header", "body", "actions"])
};

export default ModalSmg;
